import React, { useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';

import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';

import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';

import CloseIcon from '@material-ui/icons/Close';
import LeftIcon from '@material-ui/icons/ArrowBack';
import RightIcon from '@material-ui/icons/ArrowForward';
import FolderIcon from '@material-ui/icons/Folder';

import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';
import { Typography } from '@material-ui/core';

function AdvancedListItem(props) {

    return (
        <ListItem
            button
            disabled={props.disabled}
            onClick={props.onSelect}
            selected={props.selected}
            >
            <ListItemIcon>
                <FolderIcon />
            </ListItemIcon>
            <ListItemText primary={props.item.name}/>
            <ListItemSecondaryAction>
                <IconButton aria-label="Open Folder" onClick={props.onOpen}>
                    <RightIcon fontSize="small"/>
                </IconButton>
            </ListItemSecondaryAction>
        </ListItem>
    )
}

const useStyles = makeStyles(theme => ({
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogTitle: {
        padding: 0
    },
    listItemTitle: {
        paddingLeft: 6
    },
    dialogContent: {
        backgroundColor: theme.palette.grey[100],
        padding: 0
    },
    folderContainer: {
        position: 'relative',
        minHeight: 300,
        overFlowY: 'scroll',
        overflowX: 'hidden'
    },
    folder: {
        position: 'absolute',
        width: '100%',
        left: '100%',
        height: '100%'
    },
    folderEmptyContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%'
    }
}));

const transitionTime = 200;
const transition = `${transitionTime}ms ease left`;

function slideLeftIn(id) {
    const f = document.getElementById(`move-folder-${id}`);

    f.style.left = '100%';

    setTimeout(() => {
        f.style.transition = transition;
        f.style.left = '0%';

        setTimeout(() => {
            f.style.transition = null;
        }, transitionTime);
    }, 10);

}

function slideLeftOut(id) {
    const f = document.getElementById(`move-folder-${id}`);

    setTimeout(() => {
        f.style.transition = transition;
        f.style.left = '-100%';

        setTimeout(() => {
            f.style.transition = null;
        }, transitionTime);
    }, 10);
}

function slideRightIn(id) {
    const f = document.getElementById(`move-folder-${id}`);

    f.style.left = '-100%';

    setTimeout(() => {
        f.style.transition = transition;
        f.style.left = '0%';

        setTimeout(() => {
            f.style.transition = null;
        }, transitionTime);
    }, 10);
}

function slideRightOut(id) {
    const f = document.getElementById(`move-folder-${id}`);

    setTimeout(() => {
        f.style.transition = transition;
        f.style.left = '100%';

        setTimeout(() => {
            f.style.transition = null;
        }, transitionTime);
    }, 10);
}

export default function MoveItemDialog(props) {

    const classes = useStyles();
    const api = useApi();

    const { open, onClose, onMove, onClosed, item, initFolderId, folders } = props;

    const [ selectedFolderId, setSelectedFolderId ] = useState(null);
    const [ activeFolderId, setActiveFolderId ] = useState(0);

    const [ errors, setErrors ] = useState({});
    const [ loading, setLoading ] = useState(false);

    // Dialog Functions -----------------------------------------------

    const handleEnter = () => {

        const startingId = initFolderId ?? 0

        setActiveFolderId(startingId);
        setSelectedFolderId(startingId);

        document.getElementById(`move-folder-${startingId}`).style.left = '0%';
    }

    const handleExit = () => {

        setLoading(false);
        if (onClosed) onClosed();
    }

    // Calculated Variables

    const isFile = useMemo(() => {
        if (item) return 'downloads' in item;
        return false;
    }, [item]);

    const activeFolder = useMemo(() => {
        return (activeFolderId && folders.length) ? folders.find(folder => folder.id === activeFolderId) : {name: 'Club Drive'};
    }, [activeFolderId, folders])

    const topLevelFolders = useMemo(() => {
        return folders.length ? folders.filter(f => f.folder_id === 0) : [];
    }, [folders])

    // Setter Functions -----------------------------------------------

    const handleFolderNext = id => {
        handleSelect(id);
        setActiveFolderId(id);
        setSelectedFolderId(id);

        slideLeftOut(activeFolderId);
        slideLeftIn(id);
    }

    const handleFolderBack = (id = null) => {
        if (id != null) {
            handleSelect(id);
            setActiveFolderId(id);
            setSelectedFolderId(id);

            slideRightOut(activeFolderId);
            slideRightIn(id);
        }
    }

    const handleSelect = id => {
        setSelectedFolderId((id === selectedFolderId) ? activeFolderId : id);
    }

    // Post Functions -----------------------------------------------

    const postMove = () => {

        setLoading(true);

        const data = {
            id: item.id,
            file: isFile,
            folder_id: selectedFolderId
        }

        api.post('/admin/drive/move', data)
            .then(() => onMove())
            .catch(error => {
                setLoading(false);

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    }

    return (
        <Dialog
            aria-labelledby="move-item-dialog-title"
            open={open}
            fullWidth
            maxWidth="xs"
            onEnter={handleEnter}
            onClose={onClose}
            onExited={handleExit}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="edit-folder-dialog-title" className={classes.dialogTitle} disableTypography>
                <ListItem ContainerComponent="div" className={classes.listItemTitle}>
                    <ListItemIcon>
                        <IconButton
                            aria-label="Back"
                            onClick={() => handleFolderBack(activeFolder.folder_id ?? null)}
                            disabled={!("folder_id" in activeFolder)}
                            >
                            <LeftIcon fontSize="small"/>
                        </IconButton>
                    </ListItemIcon>
                    <ListItemText primary={activeFolder.name}/>
                    <ListItemSecondaryAction>
                        <IconButton
                            aria-label="Cancel"
                            onClick={onClose}
                            disabled={loading}
                            >
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </ListItemSecondaryAction>
                </ListItem>
            </DialogTitle>
            <DialogContent className={classes.dialogContent}>
                <div className={classes.folderContainer}>
                    <div className={classes.folder} id={`move-folder-0`}>
                        {folders.length ? (
                            <List>
                                {topLevelFolders.map(subFolder => (
                                    <AdvancedListItem
                                        key={subFolder.id}
                                        item={subFolder}
                                        disabled={!isFile && item && (item.id === subFolder.id)}
                                        onOpen={() => handleFolderNext(subFolder.id)}
                                        onSelect={() => handleSelect(subFolder.id)}
                                        selected={(selectedFolderId === subFolder.id)}
                                    />
                                ))}
                            </List>
                        ) : (
                            <div className={classes.folderEmptyContainer}>
                                <Typography
                                    color="textSecondary"
                                    variant="subtitle2"
                                    className={classes.folderEmpty}
                                    >
                                    Create your first folder here.
                                </Typography>
                            </div>
                        )}
                    </div>
                    {console.log(folders)}
                    {folders.map(folder => (
                        <div key={folder.id} id={`move-folder-${folder.id}`} className={classes.folder}>
                            {folder.folders_recursive && folder.folders_recursive.length ? (
                                <List>
                                    {folder.folders_recursive.map(subFolder => (
                                        <AdvancedListItem
                                            key={subFolder.id}
                                            item={subFolder}
                                            disabled={!isFile && item && (item.id === subFolder.id)}
                                            onOpen={() => handleFolderNext(subFolder.id)}
                                            onSelect={() => handleSelect(subFolder.id)}
                                            selected={(selectedFolderId === subFolder.id)}
                                        />
                                    ))}
                                </List>
                            ) : (
                                <div className={classes.folderEmptyContainer}>
                                    <Typography
                                        color="textSecondary"
                                        variant="subtitle2"
                                        className={classes.folderEmpty}
                                        >
                                        This folder is empty
                                    </Typography>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </DialogContent>
            {item &&
                <DialogActions>
                    <Grid container justify="space-between" alignItems="center">
                        <Grid item>
                            <Typography variant="subtitle2">
                                {isFile ? item.name + ' File' : item.name + ' Folder'}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={postMove}
                                color="primary"
                                variant="contained"
                                disabled={(loading || selectedFolderId === item.folder_id)}
                                >
                                {(selectedFolderId > 0 && selectedFolderId !== activeFolderId) ? 'Move Here' : 'Move'}
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            }

        </Dialog>
    );
}
